import React, { useEffect, useMemo, useRef } from "react";

import { InitDetail } from "lightgallery/lg-events";
import { GalleryItem } from "lightgallery/lg-utils";
import { LightGallery } from "lightgallery/lightgallery";
import lgVideo from "lightgallery/plugins/video";
import { VideoSource } from "lightgallery/plugins/video/types";
import lgZoom from "lightgallery/plugins/zoom";
import LightGalleryComponent from "lightgallery/react";
import { useIntl } from "react-intl";

import { RawFile } from "@mapmycustomers/shared/types/File";

import hasMimeType, { VIDEO_ANY } from "@app/util/mimeType";

import "./lightgallery.scss";

interface Props {
  contentUrl: string;
  file: RawFile;
  onHide: () => void;
}

const FilePreview: React.FC<Props> = ({ contentUrl, file, onHide }) => {
  const intl = useIntl();

  const lightGalleryRef = useRef<LightGallery>();
  const lightGalleryElement = useMemo(
    () => (
      <LightGalleryComponent
        actualSize={false} // it's a good button, but since it has no tooltip, it's confusing
        addClass="lg-media-overlap"
        allowMediaOverlap={false}
        dynamic
        dynamicEl={[]} // empty, but is populated in useEffect below
        infiniteZoom
        licenseKey={process.env.REACT_APP_LIGHTGALLERY_LICENSE_KEY as string}
        onInit={(lightGallery: InitDetail) => {
          lightGalleryRef.current = lightGallery.instance;
        }}
        plugins={[lgZoom, lgVideo]}
        showZoomInOutIcons
        zoom
        // I actually don't see they're used anywhere, but it's still better to provide them
        zoomPluginStrings={{
          viewActualSize: intl.formatMessage({
            id: "recordPane.files.preview.zoom.actualSize",
            defaultMessage: "Actual size",
          }),
          zoomIn: intl.formatMessage({
            id: "recordPane.files.preview.zoom.in",
            defaultMessage: "Zoom In",
          }),
          zoomOut: intl.formatMessage({
            id: "recordPane.files.preview.zoom.out",
            defaultMessage: "Zoom Out",
          }),
        }}
      />
    ),
    // LightGallery is too sensitive to re-rendering, so we're creating it only once
    // Please keep deps array empty. And if you need to add any listeners, do that
    // via lightGalleryRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // populate LG with gallery items when file is changed
  useEffect(() => {
    const lightGallery = lightGalleryRef.current;
    if (!lightGallery) {
      return;
    }

    if (file) {
      const isVideo = hasMimeType(file, VIDEO_ANY) && file.streamUrl;
      const galleryItem: GalleryItem = {
        alt: file.name,
        download: file.name,
        downloadUrl: isVideo ? file.streamUrl : undefined,
        // use loader src to have at least something here
        // we'll replace it with a real image once slide is active
        src: isVideo ? undefined : contentUrl,
        title: file.name,
        video: isVideo
          ? ({
              attributes: { controls: true, preload: false },
              source: [{ src: file.streamUrl, type: file.contentType }],
            } as unknown as VideoSource) // has to have this conversion because of incorrect types for the library
          : undefined,
      };
      lightGallery.refresh([galleryItem]);
    }
  }, [contentUrl, file]);

  // open gallery when file is changed
  useEffect(() => {
    lightGalleryRef.current?.openGallery(0);
  }, [file]);

  // add onHide event listener
  useEffect(() => {
    lightGalleryRef.current?.el.addEventListener("lgAfterClose", onHide);
    return () => {
      lightGalleryRef.current?.el.removeEventListener("lgAfterClose", onHide);
    };
  }, [onHide]);

  return lightGalleryElement;
};

export default FilePreview;
